<template>
  <div class="question-section flex gap-4">
    <!-- Question Header -->
    <div class="question-header">
      <h2 class="question-label">{{ label }}</h2>
      <p class="question-text" v-html="question"></p>
    </div>

    <!-- Input Box and Error Container -->
    <div class="input-wrapper flex flex-col">
      <textarea
        :value="modelValue"
        @input="handleInput"
        class="custom-textarea"
        :placeholder="placeholder"
        ref="textareaRef"
        :style="{ minHeight: getTextareaHeight + 'px' }"
        :class="{ 'error-border': errorMessage }"
      ></textarea>
      
      <!-- Error message directly below textarea -->
      <p v-if="errorMessage" class="error-text">{{ errorMessage }}</p>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch } from "vue";

const props = defineProps({
  label: {
    type: String,
    required: true,
  },
  question: {
    type: String,
    required: false,
    default: ""
  },
  placeholder: {
    type: String,
    default: "Enter your response...",
  },
  modelValue: {
    type: String,
    required: true,
  },
  errorMessage: {
    type: String,
    default: "",
  },
});

const emit = defineEmits(["update:modelValue"]);
const textareaRef = ref(null);

const getTextareaHeight = computed(() => {
  // Calculate initial height based on question length and content
  const questionLength = props.question.length;
  const baseHeight = 200;
  return Math.min(baseHeight , 450);
});

const adjustTextareaHeight = () => {
  const textarea = textareaRef.value;
  if (textarea) {
    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';
    // Set the height to match the content, but not exceed 450px
    const newHeight = Math.min(textarea.scrollHeight, 450);
    textarea.style.height = newHeight + 'px';
    
    // Only show scrollbar if content exceeds 450px
    if (textarea.scrollHeight > 450) {
      textarea.style.overflowY = 'auto';
    } else {
      textarea.style.overflowY = 'hidden';
    }
  }
};

const handleInput = (event) => {
  emit("update:modelValue", event.target.value);
  adjustTextareaHeight();
};

// Add window resize handler
onMounted(() => {
  window.addEventListener('resize', handleResize);
  // Set initial height and adjust for content
  if (textareaRef.value) {
    // Small delay to ensure content is rendered
    setTimeout(() => {
      adjustTextareaHeight();
    }, 0);
  }
});

onUnmounted(() => {
  window.removeEventListener('resize', handleResize);
});

const handleResize = () => {
  adjustTextareaHeight();
};

// Watch for changes in modelValue to adjust height when content changes externally
watch(() => props.modelValue, () => {
  adjustTextareaHeight();
});
</script>

<style scoped>
.question-section {
  margin-bottom: 24px;
  align-items: flex-start; /* Changed from center to allow error to flow naturally */
  min-height: fit-content;
  display: flex;
  width: 100%;
  gap: 14px;
}

.question-header {
  flex: 3;
  gap: 4px;
  display: flex;
  flex-direction: column;
  padding: 24px 0px;
  height: fit-content;
  background: transparent;
  justify-content: center;
  min-width: 0;
}

.question-label {
  font-family: "Open Sans", sans-serif;
  font-weight: 700;
  font-size: 16px;
  line-height: 87%;
  color: #00423d;
  margin-bottom: 8px;
}

.question-text {
  font-family: "Open Sans", sans-serif;
  font-weight: 400;
  font-size: 16px;
  line-height: 141%;
  color: #414141;
  word-wrap: break-word;
  height: fit-content;
  margin-bottom: 8px;
  white-space: pre-line;
}

.input-wrapper {
  flex: 7;
  height: fit-content;
  display: flex;
  flex-direction: column; /* Changed to column to stack textarea and error */
  min-width: 0;
}

.custom-textarea {
  width: 100%;
  min-height: 200px;
  max-height: 450px;
  padding: 12px;
  font-family: "Open Sans", sans-serif;
  font-size: 14px;
  line-height: 22px;
  color: #1c1c1c;
  resize: none;
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  background: linear-gradient(
      0deg,
      rgba(245, 246, 250, 0.7),
      rgba(245, 246, 250, 0.7)
    ),
    linear-gradient(0deg, rgba(245, 246, 250, 0.7), rgba(245, 246, 250, 0.7));
  overflow-y: hidden; /* Start with hidden overflow */
  transition: height 0.1s ease-out;
}

.custom-textarea:focus {
  outline: none;
  border-color: #e2e2e2;
}

.custom-textarea::placeholder {
  color: #999999;
}

.error-text {
  color: #dc2626;
  font-size: 14px;
  font-family: "Open Sans", sans-serif;
  margin-top: 6px;
  margin-bottom: 0;
  padding-left: 4px;
}

.error-border {
  border-color: #dc2626;
}
</style>